/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState } from 'react';

enum Status {
  LOADING = 'Loading',
  READY = '',
}

const useGeolocationData = () => {
  const [GeolocationResp, setGeolocationResp] = useState(null);
  const [GeolocationErrors, setGeolocationErrors] = useState('');
  const [GeolocationStatus, setGeolocationStatus] = useState(Status.READY);

  const getErrorMessage = useCallback((error: GeolocationPositionError) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'User denied the request for Geolocation.';
      case error.POSITION_UNAVAILABLE:
        return 'Location information is unavailable.';
      case error.TIMEOUT:
        return 'The request to get user location timed out.';
      default:
        return error.message;
    }
  }, []);

  const getPosition = useCallback(async () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          resolve(position);
        },
        (error: GeolocationPositionError) => {
          reject(error);
        },
      );
    });
  }, []);

  const GeolocationAPI = useCallback(async (): Promise<GeolocationPosition> => {
    const geolocationAPI = navigator.geolocation;
    if (!geolocationAPI) {
      setGeolocationErrors("Geolocation isn't available");
    }
    const res = await getPosition()
      .then((position: GeolocationPosition) => {
        return position;
      })
      .catch((err: GeolocationPositionError) => {
        setGeolocationErrors(getErrorMessage(err));
        console.log('error in geolocationapi', err);
        return null;
      });

    setGeolocationStatus(Status.READY);
    return res;
  }, [getPosition, getErrorMessage]);

  const processGeolocation = useCallback(async () => {
    setGeolocationResp(null);
    setGeolocationStatus(Status.LOADING);
    setGeolocationErrors('');
    const res = await GeolocationAPI();
    return res;
  }, [GeolocationAPI]);

  return { GeolocationResp, GeolocationStatus, GeolocationErrors, processGeolocation };
};

export default useGeolocationData;
